import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  googleAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Content } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core';
import { Alert, Box, Button, Flex, Link, Skeleton } from '@backstage/ui';
import { RunPanel } from '../requests/RunPanel';
import type { Run } from '../requests/RunPanel';
import { applyImplies, humanise } from './schemaForm';
import type { JsonValue } from './schemaForm';
import { PipelineForm } from './PipelineForm';
import {
  describeIssue,
  fetchPipelineManifest,
  issuesFromRejection,
  relevantFields,
  requestBody,
  validateRequest,
} from './pipelineManifest';
import type { FieldIssue, PipelineDescriptor } from './pipelineManifest';
import { CALLER_ID_TOKEN_HEADER } from '../requests/callerToken';

/**
 * Where a run is watched when the backend did not say. Only a fallback: the
 * backend derives the link from the Control it is actually configured for.
 */
const CONTROL_FALLBACK = 'https://control.answerrocket.com';

/**
 * Answers carried in the URL — `/create/templates/new-env?cloud=aws`.
 *
 * So a link can say WHICH new environment ("an AWS account for this client")
 * without a page per variant. Only top-level string fields, and an enum only
 * takes a member it actually has: a URL is a suggestion, not a way to put a
 * value in front of Control that the form itself would not offer.
 */
function prefillFrom(
  pipeline: PipelineDescriptor,
  params: URLSearchParams,
): Record<string, JsonValue> {
  const values: Record<string, JsonValue> = {};
  for (const [name, schema] of Object.entries(
    pipeline.schema.properties ?? {},
  )) {
    const raw = params.get(name);
    if (!raw || (schema.type && schema.type !== 'string')) continue;
    if (schema.enum && !schema.enum.includes(raw)) continue;
    values[name] = raw;
  }
  return values;
}

type Submitted = { run?: Run; runUrl?: string };

/**
 * `/create/templates/:pipelineId` — any pipeline Control publishes, as a form.
 *
 * PORTAL.md §4.1: "Pipelines that touch no repository need no template at
 * all." This page knows no pipeline. It reads the manifest, draws the one it
 * was asked for (schema + `ui`), and posts the answers back through
 * `/api/control/pipelines/:id/requests`, which looks the endpoint up in the
 * same manifest and forwards the CALLER's Google ID token — never a service
 * token. A pipeline Control ships tomorrow works here the day it ships
 * (§8: "adding a pipeline should need no Catalog PR").
 */
/** An answer as the summary column shows it: an option's label, or a count. */
function describeAnswer(
  value: JsonValue,
  options?: Record<string, { label: string }>,
): string {
  if (typeof value === 'string' && options?.[value]) {
    return options[value].label;
  }
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (typeof value === 'object' && value !== null) {
    return `${Object.keys(value).length} set`;
  }
  return String(value);
}

/**
 * Layout after Control's own (since retired) request pages and the house kit:
 * eyebrow + display title + lead, then the form's section cards on the left
 * and a sticky column on the right that says what is being asked for, what
 * happens next, and holds the one button. The button used to sit under the
 * last field, a scroll away from the answers it was submitting.
 */
const useStyles = makeStyles({
  page: { maxWidth: 1180 },
  header: { marginBottom: 24, maxWidth: 760 },
  eyebrow: {
    fontFamily: 'var(--ar-font-mono)',
    fontSize: '0.6875rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
  },
  title: {
    fontFamily: 'var(--ar-font-display)',
    fontSize: '1.875rem',
    fontWeight: 620,
    letterSpacing: '-0.02em',
    color: 'var(--ar-fg)',
    margin: '6px 0 8px',
  },
  lead: {
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: 'var(--ar-fg-2)',
    margin: 0,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
    marginTop: 14,
  },
  badge: {
    fontFamily: 'var(--ar-font-mono)',
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '3px 8px',
    borderRadius: 8,
    border: '1px solid var(--ar-border-strong)',
    color: 'var(--ar-fg-2)',
    background: 'var(--ar-surface-2)',
  },
  grid: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'minmax(0, 1fr)',
    '@media (min-width: 1100px)': {
      gridTemplateColumns: 'minmax(0, 1fr) 320px',
    },
  },
  main: { minWidth: 0 },
  aside: {
    '@media (min-width: 1100px)': {
      position: 'sticky',
      top: 24,
      alignSelf: 'start',
    },
  },
  summary: {
    border: '1px solid var(--ar-border)',
    borderRadius: 14,
    background: 'var(--ar-surface)',
    boxShadow: '0 6px 24px rgba(17, 24, 39, 0.05)',
    padding: '18px 20px 20px',
  },
  summaryEmpty: {
    fontSize: '0.8125rem',
    color: 'var(--ar-fg-3)',
    margin: '10px 0 16px',
  },
  summaryList: { margin: '10px 0 16px' },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '7px 0',
    borderBottom: '1px solid var(--ar-border-soft)',
    fontSize: '0.8125rem',
    '& dt': { color: 'var(--ar-fg-2)' },
    '& dd': {
      margin: 0,
      fontWeight: 600,
      color: 'var(--ar-fg)',
      textAlign: 'right',
      overflowWrap: 'anywhere',
    },
  },
  next: {
    margin: '0 0 18px',
    paddingLeft: 18,
    fontSize: '0.8125rem',
    lineHeight: 1.7,
    color: 'var(--ar-fg-2)',
  },
  submit: { width: '100%', justifyContent: 'center' },
  fine: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: 'var(--ar-fg-3)',
    margin: '10px 0 0',
    textAlign: 'center',
  },
});

export const PipelineRequestPage = () => {
  const classes = useStyles();
  const { pipelineId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const googleAuth = useApi(googleAuthApiRef);

  const [pipeline, setPipeline] = useState<PipelineDescriptor>();
  const [known, setKnown] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string>();
  const [values, setValues] = useState<Record<string, JsonValue>>({});
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [submitError, setSubmitError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<Submitted>();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const pipelines = await fetchPipelineManifest(discovery, fetchApi);
        if (cancelled) return;
        const found = pipelines.find(p => p.id === pipelineId);
        setKnown(pipelines.map(p => p.id));
        if (!found) {
          setLoadError(`Control publishes no pipeline "${pipelineId}".`);
          return;
        }
        setPipeline(found);
        setValues(prefillFrom(found, searchParams));
      } catch (e) {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Once per pipeline. The query string is a starting point, not a binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId, discovery, fetchApi]);

  const set = useCallback(
    (name: string, value: JsonValue | undefined) => {
      setValues(previous => {
        const next = { ...previous };
        if (value === undefined || value === '') delete next[name];
        else next[name] = value;
        // Answering one field can fill in others; Control publishes which.
        return applyImplies(pipeline?.ui.fields?.[name], value, next);
      });
      // An answer being edited is an answer being fixed; its old complaint
      // should not sit under it.
      setIssues(previous => previous.filter(i => i.field !== name));
    },
    [pipeline],
  );

  const fieldErrors = useMemo(
    () =>
      Object.fromEntries(
        issues.filter(i => i.field).map(i => [i.field!, i.message]),
      ),
    [issues],
  );

  const submit = async () => {
    if (!pipeline || submitting) return;
    setSubmitError(undefined);

    const local = validateRequest(pipeline, values);
    if (local.length) {
      setIssues(local);
      return;
    }

    setSubmitting(true);
    try {
      // The caller's OWN identity, read at the click — a popup opened from a
      // click is allowed where one opened on mount is blocked. Control
      // evaluates THEIR rbac and records THEM as the requester; there is no
      // fallback to any identity of the portal's.
      const idToken = await googleAuth.getIdToken();
      if (!idToken) {
        setSubmitError(
          'Your session did not include a Google ID token. Control acts as the ' +
            'person asking, so the request cannot be made without one. Press ' +
            'Submit again, or sign out and back in.',
        );
        return;
      }
      const baseUrl = await discovery.getBaseUrl('control');
      const response = await fetchApi.fetch(
        `${baseUrl}/pipelines/${encodeURIComponent(pipeline.id)}/requests`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [CALLER_ID_TOKEN_HEADER]: idToken,
          },
          body: JSON.stringify(requestBody(pipeline, values)),
        },
      );
      const body = await response.json().catch(() => undefined);
      // Control's own rejection — `{ error: string, issues[] }` — as opposed
      // to a Backstage error envelope, whose `error` is an object.
      const rejection = body as
        | { error?: unknown; issues?: unknown }
        | undefined;
      if (
        response.status === 400 &&
        (Array.isArray(rejection?.issues) ||
          typeof rejection?.error === 'string')
      ) {
        setIssues(issuesFromRejection(body));
        return;
      }
      if (!response.ok) {
        const message =
          (body as { error?: { message?: string } } | undefined)?.error
            ?.message ?? `${response.status} ${response.statusText}`;
        setSubmitError(message);
        return;
      }
      setIssues([]);
      setSubmitted(body as Submitted);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <Content>
        <Box style={{ maxWidth: 760 }}>
          <Alert
            status="warning"
            title="Could not read what Control accepts"
            description={`${loadError}${
              known.length ? ` It publishes: ${known.join(', ')}.` : ''
            } Without the manifest this form would be guessing, so it shows nothing rather than something wrong.`}
          />
          <Box mt="4">
            <Link href="/create/templates">Back to Start</Link>
          </Box>
        </Box>
      </Content>
    );
  }

  if (!pipeline) {
    return (
      <Content>
        <Flex direction="column" gap="3" style={{ maxWidth: 760 }}>
          <Skeleton style={{ height: 40 }} />
          <Skeleton style={{ height: 56 }} />
          <Skeleton style={{ height: 56 }} />
        </Flex>
      </Content>
    );
  }

  if (submitted) {
    const runId = submitted.run?.id;
    const runUrl =
      submitted.runUrl ??
      (runId ? `${CONTROL_FALLBACK}/pipelines/${runId}` : CONTROL_FALLBACK);
    return (
      <Content>
        <Box style={{ maxWidth: 760 }}>
          <Alert
            status="success"
            title={`${pipeline.title}: requested`}
            description={
              runId
                ? `Control accepted it as run ${runId}. Nothing in the cloud changes until the gate clears.`
                : 'Control accepted it. Nothing in the cloud changes until the gate clears.'
            }
          />
          <Flex gap="4" style={{ marginTop: 16, marginBottom: 16 }}>
            <Link href={runUrl}>Open the run in Control</Link>
            <Link href="/create/request">All my requests</Link>
          </Flex>
          {runId && <RunPanel runId={runId} initial={submitted.run} />}
        </Box>
      </Content>
    );
  }

  // What the person has said so far, in the manifest's own words — the right
  // column's summary. Only answered, still-relevant fields: a field the form
  // stopped asking (projectId after switching to AWS) is not part of the
  // request and must not look like it is.
  const relevant = relevantFields(pipeline, values);
  const answered = Object.entries(values)
    .filter(([name]) => relevant.has(name))
    .map(([name, value]) => {
      const ui = pipeline.ui.fields?.[name];
      const schema = pipeline.schema.properties?.[name];
      const shown = describeAnswer(value, ui?.options);
      return {
        name,
        label: ui?.label ?? schema?.title ?? humanise(name),
        shown,
      };
    });

  return (
    <Content>
      <div className={classes.page}>
        <header className={classes.header}>
          <div className={classes.eyebrow}>Create · Request</div>
          <h1 className={classes.title}>{pipeline.title}</h1>
          <p className={classes.lead}>{pipeline.summary}</p>
          <div className={classes.meta}>
            {pipeline.approval?.policy === 'devops' && (
              <span className={classes.badge}>Reviewed by DevOps</span>
            )}
            {pipeline.status && pipeline.status !== 'ga' && (
              <span className={classes.badge}>{pipeline.status}</span>
            )}
            {pipeline.docs && (
              <Link href={pipeline.docs} variant="body-small">
                What these options mean ↗
              </Link>
            )}
          </div>
        </header>

        <div className={classes.grid}>
          <div className={classes.main}>
            {issues.length > 0 && (
              <Box mb="4" role="alert">
                <Alert
                  status="danger"
                  title="Fix these, then submit again. Nothing was requested."
                  description={issues
                    .map(issue => describeIssue(pipeline, issue))
                    .join(' · ')}
                />
              </Box>
            )}
            <PipelineForm
              pipeline={pipeline}
              values={values}
              onSet={set}
              errors={fieldErrors}
            />
          </div>

          <aside className={classes.aside}>
            <div className={classes.summary}>
              <div className={classes.eyebrow}>Your request</div>
              {answered.length === 0 ? (
                <p className={classes.summaryEmpty}>
                  Answers appear here as you fill the form in.
                </p>
              ) : (
                <dl className={classes.summaryList}>
                  {answered.map(row => (
                    <div key={row.name} className={classes.summaryRow}>
                      <dt>{row.label}</dt>
                      <dd>{row.shown}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <ol className={classes.next}>
                <li>Control opens the pull requests and a plan.</li>
                <li>DevOps reviews and approves.</li>
                <li>It is created; you follow it under My requests.</li>
              </ol>
              {submitError && (
                <Box mb="3">
                  <Alert
                    status="warning"
                    title="The request was not sent"
                    description={submitError}
                  />
                </Box>
              )}
              <Button
                variant="primary"
                onPress={() => void submit()}
                isDisabled={submitting}
                className={classes.submit}
              >
                {submitting ? 'Requesting…' : 'Submit request'}
              </Button>
              <p className={classes.fine}>
                Sent to Control as you. Nothing in the cloud changes until it is
                approved.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </Content>
  );
};
